import { Navigate, createBrowserRouter } from 'react-router';
import { AppError } from '../shell/AppError.js';
import { Shell } from '../shell/Shell.js';
import { Gate, RequireAnonymous, RequireSession } from './gate.js';
import { Callback } from '../screens/auth/Callback.js';
import { Forgot, Sent } from '../screens/auth/Forgot.js';
import { SignInFlow } from '../screens/auth/SignInFlow.js';
import { Welcome } from '../screens/Welcome.js';
import { InDevelopment } from '../screens/modules/InDevelopment.js';
import { Tenants } from '../screens/admin/Tenants.js';
import { TenantDetail } from '../screens/admin/TenantDetail.js';
import { NewTenant } from '../screens/admin/NewTenant.js';
import { Staff } from '../screens/admin/Staff.js';
import { RolesPicker, TenantRoles } from '../screens/admin/Roles.js';
import { Organization } from '../screens/org/Organization.js';
import { HandoffConfirm } from '../screens/org/HandoffConfirm.js';
import { Demo } from '../screens/Demo.js';
import { REGISTERED_MODULES } from '@titan/modules';
import { moduleForPath } from '@titan/shared';
import { NAV_MODEL_STAFF } from '@titan/modules';
import { moduleRouteObjects } from '../modules/registry.js';

/**
 * Product modules without code yet: header band plus the in-development state. Modules
 * registered in `@titan/modules` mount their own screens instead (see modules/registry).
 */
const modules: Array<{ path: string; eyebrow: string; title: string; description: string }> = [
  { path: '/operations/war-room', eyebrow: 'general operations · war room', title: 'War room', description: 'Live incident and operations view across your accounts.' },
  { path: '/operations/security', eyebrow: 'general operations · security', title: 'Security', description: 'Posture and findings from Security Hub and GuardDuty.' },
  { path: '/operations/finops', eyebrow: 'general operations · finops', title: 'FinOps Analysis', description: 'Cost analysis from Cost Explorer and your allocation tags.' },
  { path: '/operations/dashboards', eyebrow: 'general operations · dashboards', title: 'Dashboards', description: 'Index of the dashboards available to your organization.' },
  { path: '/modernization/workloads', eyebrow: 'modernization space · workloads', title: 'Workloads', description: 'Open a workload, run an agentic assessment, compare pathways.' },
  { path: '/modernization/case', eyebrow: 'modernization space · business case', title: 'Business case', description: 'TCO and business KPIs for one pathway.' },
  { path: '/modernization/waves', eyebrow: 'modernization space · execution', title: 'Waves', description: 'Execution plan and results for an approved pathway.' },
  { path: '/roi/tco', eyebrow: 'roi analysis · tco calculators', title: 'TCO Calculators', description: 'Comparative models built by Costy from invoices and inventory exports.' },
  { path: '/roi/tco/new', eyebrow: 'roi analysis · ask costy', title: 'Ask Costy', description: 'The conversation that builds a calculator from your evidence.' },
  { path: '/roi/calculator', eyebrow: 'roi analysis · roi calculator', title: 'ROI Calculator', description: 'Business value model per AWS service, with industry, maturity and scenario levers.' },
  { path: '/ai/agents', eyebrow: 'ai workspace · catalog', title: 'Agents Catalog', description: 'The agents deployed on your accounts, discovered from tags.' },
  { path: '/ai/governance', eyebrow: 'ai workspace · governance', title: 'AI Governance', description: 'Posture across the whole AI estate: inventory, identity, least privilege, observability, compliance.' },
  { path: '/ai/agents/register', eyebrow: 'ai workspace · register', title: 'Register an agent', description: 'Bring a deployed agent into the catalog.' },
  { path: '/tickets', eyebrow: 'ticketing platform · tickets', title: 'Tickets', description: 'Queue with KPIs, filters and SLA tracking.' },
  { path: '/tickets/rules', eyebrow: 'ticketing platform · automation', title: 'Automation rules', description: 'When and then rules, proposed by Rex from plain language.' },
  { path: '/tickets/analytics', eyebrow: 'ticketing platform · analytics', title: 'Analytics', description: 'SLA compliance, resolution, CSAT and the cost comparison against the tool being replaced.' },
  { path: '/tickets/rex', eyebrow: 'ticketing platform · rex', title: 'Rex preferences', description: 'Autonomy and escalation settings for Rex.' },
];

export const router = createBrowserRouter([
  {
    element: <RequireAnonymous />,
    errorElement: <AppError />,
    children: [
      { path: '/auth/sign-in', element: <SignInFlow /> },
      { path: '/auth/forgot', element: <Forgot /> },
      { path: '/auth/sent', element: <Sent /> },
      { path: '/auth/callback', element: <Callback /> },
      { path: '/auth/*', element: <Navigate to="/auth/sign-in" replace /> },
    ],
  },
  {
    element: <RequireSession />,
    errorElement: <AppError />,
    children: [
      {
        element: <Gate />,
        children: [
          {
            element: <Shell />,
            errorElement: <AppError />,
            children: [
              { path: '/', element: <Welcome /> },
              ...modules
                .filter((m) => !REGISTERED_MODULES.includes(moduleForPath(m.path, NAV_MODEL_STAFF) ?? 'dash') || !moduleForPath(m.path, NAV_MODEL_STAFF))
                .map((m) => ({ path: m.path, element: <InDevelopment eyebrow={m.eyebrow} title={m.title} description={m.description} /> })),
              ...moduleRouteObjects(),
              { path: '/admin/tenants', element: <Tenants /> },
              { path: '/admin/tenants/new', element: <NewTenant /> },
              { path: '/admin/tenants/:id', element: <TenantDetail /> },
              { path: '/admin/tenants/:id/roles', element: <TenantRoles /> },
              { path: '/admin/staff', element: <Staff /> },
              { path: '/admin/roles', element: <RolesPicker /> },
              { path: '/organization', element: <Organization /> },
              { path: '/organization/handoff', element: <HandoffConfirm /> },
              { path: '/demo', element: <Demo /> },
              { path: '*', element: <Navigate to="/" replace /> },
            ],
          },
        ],
      },
    ],
  },
]);
